<template>
    <div 
        class="object-grid-wrapper"
        :class="`object-grid-wrapper-${modelClass}`"
    >
        <div v-if="gridLoadedEmpty" key="gridLoadedEmpty" class="h-100">
            <slot name="empty" v-bind="emptySlotProps">
                <div class="d-flex w-100 min-height-200 border rounded-lg bg-white-40 align-items-center justify-content-center">
                    <div class="text-muted text-uppercase font-weight-500">{{ finalEmptyText }}</div>
                </div>
            </slot>
        </div>

        <div v-else-if="!providerReady" key="providerLoading" class="h-100">
            <slot name="loading" v-bind="loadingSlotProps">
                <busy-spinner :text="loadingText"></busy-spinner>
            </slot>
        </div>

        <div 
            v-if="providerReady" 
            class="w-100 h-100 d-flex flex-column" 
        >
            <div class="py-2 d-flex justify-content-between align-items-end">
                <div class="d-flex gap-3 align-items-end">  <!-- LEFT SIDE - quick filters -->
                    <div v-if="visibleExtraFilters && visibleExtraFilters.length" class="quick-filter" :style="{width: $localeIsCJK ? 'auto' : 'min-content'}">
                        <div class="h6 text-primary-darker text-wrap">{{ $t('objectGrid.quickFilters') }}</div>
                    </div>
                    <div v-for="flt in visibleExtraFilters" :key="flt.field" class="quick-filter">
                        <label class="filter-label">{{ flt.label }}</label>
                        <PrimeDropdown 
                            :value="extraFilterValueKeys[flt.field]"
                            @input="handleExtraFilterInput(flt.field, $event)"
                            :options="flt.options" 
                            :placeholder="flt.placeholder"
                            :showClear="true"
                            optionLabel="label"
                            optionValue="key"
                            dataKey="key"
                            scrollHeight="250px"
                            :class="extraFilterValueKeys[flt.field] !== flt.emptyValue ? 'font-weight-bold' : ''"
                        ></PrimeDropdown>
                    </div>

                    <!-- PRESETS -->
                    <div v-if="!hidePresets && gridPresets && gridPresets.length" class="quick-filter">
                        <label class="filter-label">{{ $t('objectGrid.presets') }}</label>
                        <div class="p-inputgroup">
                            <PrimeCascadeSelect 
                                :value="gridPresetSelected" 
                                @input="handlePresetSelection"
                                :options="gridPresets" 
                                dataKey="key"
                                optionLabel="label" 
                                optionGroupLabel="label"
                                :optionGroupChildren="['items']"
                                :placeholder="$t('objectGrid.viewAll')"
                                style="min-width: 14rem;"
                                :class="gridPresetSelected ? 'font-weight-bold' : ''"
                            >
                                <template #value="slotProps">
                                    <div class="">
                                        {{ hasAnyFiltersOrGrouping ? (lastSetPresetIsCurrent ? (slotProps.value ? slotProps.value.label : slotProps.placeholder ) : 'Custom') : slotProps.placeholder }}
                                    </div>
                                </template>
                            </PrimeCascadeSelect>
                            <PrimeButton 
                                icon="pi pi-times" 
                                :disabled="!gridPresetSelected" 
                                :class="gridPresetSelected ? '' : 'p-button-secondary p-button-outlined'" 
                                class="p-button-sm"
                                @click="resetPresetSelection"
                            />
                        </div>
                    </div>

                    <div class="text-center">  <!-- || hasAnyExternalFiltersApplied -->
                        <div 
                            v-if="gridHasAnyFilters" 
                            class="text-primary-darker special-text font-size-70 text-center mb-1"
                        >
                            <b-icon icon="funnel-fill" class="mr-1"></b-icon>
                            {{ $t('objectGrid.filtersApplied') }}
                        </div>
                        <b-button 
                            :variant="isReset ? 'subtle-secondary' : 'sharp-primary'" 
                            size="sm" 
                            @click="resetFilters"
                        >
                            <b-icon icon="funnel" class="mr-1"></b-icon>
                            {{ $t('objectGrid.resetFiltersGrouping') }}
                        </b-button>
                    </div>

                </div>
                <div v-if="providerReady">  <!-- RIGHT SIDE - BUTTONS / TOOLBAR -->
                    <div class="d-flex gap-2 justify-content-end">
                        <b-button variant="subtle-primary" size="sm" :disabled="anyRowsLoading" @click="reloadRows"><b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>{{ $t('objectGrid.refresh') }}</b-button>
                        <!-- <b-button-group v-if="$currentUserInfo.user_is_staff" size="sm" class="">
                            <b-button variant="subtle-primary" @click="dumpGrid">Dump Grid</b-button>
                        </b-button-group> -->
                        <b-button-group v-if="allowServerSideExcelExport" size="sm" class="">
                            <b-button variant="subtle-primary" :disabled="anyRowsLoading || hasExcelError || isLoadingExcel || gridHasAnyGrouping" class="d-flex align-items-center gap-2" @click="startServerSideExcelDownload">
                                <!-- <b-icon variant="success" icon="file-earmark-excel" class="mr-1"></b-icon> -->
                                <span class="fiv-sqo fiv-icon-xls font-size-100"></span>
                                <span>{{ $t('objectGrid.exportToExcel') }}</span>
                            </b-button>
                        </b-button-group>
                    </div>
                    <div class="d-flex gap-2 mt-1 justify-content-end">
                        <b-button-group size="sm" class="">
                            <b-button variant="subtle-primary" :disabled="anyRowsLoading" @click="resetColumns"><b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>{{ $t('objectGrid.resetColumns') }}</b-button>
                            <!-- <b-button variant="subtle-primary" :disabled="anyRowsLoading" @click="sizeColsToFit"><b-icon icon="arrow-left-short"></b-icon><b-icon icon="arrow-right-short" class="mr-1"></b-icon>Size Columns to Fit</b-button> -->
                            <b-button variant="subtle-primary" :disabled="anyRowsLoading" @click="autoSizeAll"><b-icon icon="arrow-left-short"></b-icon><b-icon icon="arrow-right-short" class="mr-1"></b-icon>{{ $t('objectGrid.autoSizeColumns') }}</b-button>
                        </b-button-group>
                    </div>
                </div>
            </div>

            <div class="flex-fill" :style="{maxHeight: gridMaxHeight}">
                <ag-grid-vue
                    ref="theGrid"
                    class="ag-theme-alpine w-100 h-100"
                    :gridOptions="gridOptions"
                    :columnDefs="colDefs"
                    rowModelType="serverSide"
                    serverSideStoreType="partial"
                    :cacheBlockSize="localBlockSize"
                    :serverSideDatasource="gridProvider"
                    :blockLoadDebounceMillis="blockLoadDebounceMillis"
                    :sideBar="sideBar"
                    :statusBar="statusBar"
                    :defaultColDef="defaultColDef"
                    :columnTypes="columnTypes"
                    :context="gridContext"
                    :animateRows="true"
                    :autoGroupColumnDef="autoGroupColumnDef"
                    :rowGroupPanelShow="rowGroupPanelShow"
                    :enableRangeSelection="enableRangeSelection"
                    :suppressContextMenu="suppressContextMenu"
                    :localeText="gridLocaleText"
                    @grid-ready="onGridReady"
                    @first-data-rendered="onFirstDataRendered"
                    @model-updated="onGridModelUpdated"
                    @row-data-changed="onRowDataChanged"
                    @filter-changed="handleFilterChanged"
                    @columnRowGroupChanged="handleColumnRowGroupChanged"
                    @rowGroupOpened="onRowGroupOpened"
                    @cellDoubleClicked="onCellDoubleClicked"
                    @columnResized="onColumnResized"
                    @columnVisible="onColumnVisible"
                    @columnMoved="onColumnMoved"
                    @gridColumnsChanged="onGridColumnsChanged"
                    @displayedColumnsChanged="onDisplayedColumnsChanged"
                ></ag-grid-vue>
            </div>
        </div>

        <!-- <div class="d-flex gap-3">
            <div class="">
                <h5>gridStats</h5>
                <pre>{{ gridStats }}</pre>
            </div>
            <div class="">
                <h5>loadingRows</h5>
                <pre>{{ loadingRows }}</pre>
            </div>
            <div>
                <h5>lastSetFiltersGrouping</h5>
                <pre>{{ lastSetFiltersGrouping }}</pre>
            </div>
            <div class="">
                <h5>currentFilters</h5>
                <pre>{{ currentFilters }}</pre>
            </div>
            <div class="">
                <h5>currentGroupingCols</h5>
                <pre>{{ currentGroupingCols }}</pre>
            </div>
            <div class="">
                <h5>gridPresetSelected</h5>
                <pre>{{ gridPresetSelected }}</pre>
            </div>
        </div> -->

    </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import toInteger from 'lodash/toInteger';
import debounce from 'lodash/debounce';
import GridProvider from './gridProvider';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import { 
    SmStateRenderer, 
    SsrmStatusBarComponent, 
    ProgressBarComponent,
    ProgressStepComponent,
    EmptyGridOverlayComponent,
    BootstrapPhoneNumberRenderer,
    PrefetchedOverdueNodesRenderer,
    PrefetchedActiveNodesRenderer,
    CompanyTagListRenderer,
    CompanyTagSingleRenderer,
    SplTagListRenderer,
    SplTagSingleRenderer,
    RfxListRenderer,
    ContactFullNameRenderer,
    PortalAccessBadgeRenderer,
    EmailAddressRenderer,
    DefaultContactRenderer,
    TenantRolesRenderer,
    CompactDateTimeRenderer,
    ActionCellRenderer,
    CompanySiteTypeRenderer,
} from './gridRenderers';
import columnTypes from './colTypes';
import PrimeCascadeSelect from 'primevue/cascadeselect';
import PrimeButton from 'primevue/button';
import PrimeDropdown from 'primevue/dropdown';
import { DateTime } from 'luxon';
import getAgGridLocaleText from '@/localization/shared/agGrid';

// use like this:  <ObjectGrid v-if="$splEnvIsDev" modelClass="QualityIssue" class="my-5"></ObjectGrid>

// LATER - make child components for external filters 

// const createdTimeframeFilter = { // NOTE - dates/datetimes in ag-grid should be JS format Date objects 
const getCreatedTimeframeFilter = ({label, placeholder, options}) => {
    return {
        field: 'created',
        label: label,
        filterType: 'date',
        emptyValue: null,  // use null for no option set
        placeholder: placeholder,
        // TODO: initialValue (some non-empty initialValue) use 'key' of option to set some option -- TODO - handle initialValue being some actual filter... need to check before initial fetch?
        //resetValue: 'all',  // use 'key' of option 
        options: options,
        // optionsOld: [  // we use 'key' as value in dropdown 
        //     /* {
        //         key: 'all',
        //         label: 'Select...',
        //         filterParams: null,
        //     }, */
        //     {
        //         key: 'last_24_hours',
        //         label: 'Last 24 Hours',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {hours: 24},
        //             dateFromDirection: 'past',
        //         },
        //     },
        //     {
        //         key: 'last_48_hours',
        //         label: 'Last 48 Hours',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {hours: 48},
        //             dateFromDirection: 'past',
        //         },
        //     },
        //     {
        //         key: 'last_7_days',
        //         label: 'Last 7 Days',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {days: 7},
        //             dateFromDirection: 'past',
        //         },
        //     },
        //     {
        //         key: 'last_30_days',
        //         label: 'Last 30 Days',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {days: 30},
        //             dateFromDirection: 'past',
        //         },
        //     },
        //     {
        //         key: 'last_6_months',
        //         label: 'Last 6 Months',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {months: 6},
        //             dateFromDirection: 'past',
        //         },
        //     },
        //     {
        //         key: 'last_1_year',
        //         label: 'Last 1 Year',
        //         filterParams: {
        //             type: 'greaterThan',
        //             dateFromDuration: {years: 1},
        //             dateFromDirection: 'past',
        //         },
        //     },
        // ],
        buildFilter(selectedKey) {
            // call this when actually selected so datetimes are based on CURRENT datetime 
            const selectedOption = this.options.find(opt => opt.key == selectedKey);
            if (!selectedOption || !selectedOption.filterParams) return null;
            const params = selectedOption.filterParams;
            const filterObj = {
                filterType: 'date',
                type: params.type,  // 'equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange'
            };
            const currentDateTime = DateTime.utc();
            // datetimes in ag-grid filters are STRINGS (but internal comparison is done with JS Date objects)
            // and the string representation is NOT TZ AWARE (at least using the built-in ag-grid datepickers):  '2024-01-01 00:00:00' 
            if (params.dateFromDuration && params.dateFromDirection == 'past') {
                filterObj.dateFrom = currentDateTime.minus(params.dateFromDuration).toJSON();
            }
            if (params.dateFromDuration && params.dateFromDirection == 'future') {
                filterObj.dateFrom = currentDateTime.plus(params.dateFromDuration).toJSON();
            }
            if (params.dateToDuration && params.dateToDirection == 'past') {
                filterObj.dateTo = currentDateTime.minus(params.dateToDuration).toJSON();
            }
            if (params.dateToDuration && params.dateToDirection == 'future') {
                filterObj.dateTo = currentDateTime.plus(params.dateToDuration).toJSON();
            }
            return filterObj;  // caller should nest this in the 'field' key 
        }
    };
};


// const isTuInvolvedFilter = { 
const getIsTuInvolvedFilter = ({label, placeholder, options}) => {
    return {
        field: 'annotated_is_tu_involved',  // must match annotated field from backend 
        label: label,
        filterType: 'set',  // 'set' filter, same as we use for boolean cols -- values should be 'true' / 'false' (strings)
        emptyValue: null,  // use null for no option set
        placeholder: placeholder,
        // TODO: initialValue (some non-empty initialValue) use 'key' of option to set some option -- TODO - handle initialValue being some actual filter... need to check before initial fetch?
        //resetValue: 'all',  // use 'key' of option 
        options: options,
        // optionsOld: [  // we use 'key' as value in dropdown 
        //     /* {
        //         key: 'all',
        //         label: 'Select...',
        //         filterParams: null,
        //     }, */
        //     {
        //         key: 'true',  // IS INVOLVED
        //         label: 'Involved',
        //     },
        //     {
        //         key: 'false',
        //         label: 'Not Involved',
        //     },
        // ],
        buildFilter(selectedKey) {
            // call this when actually selected so datetimes are based on CURRENT datetime 
            const selectedOption = this.options.find(opt => opt.key == selectedKey);
            if (!selectedOption) return null;
            const filterObj = {
                filterType: 'set',
                values: [selectedKey],  // must be array
            };
            return filterObj;  // caller should nest this in the 'field' key 
        }
    };
};

// TODO - build external filter for status / sm_state -- show only the ones visible to the TU, and allow multiple selection, with a default that doesn't show active ones?

const rowGroupingParams = {
    // keys are the value of prop rowGrouping -- values are props on the grid
    panel: {  // always show the rowGroup panel and drag-and-drop cols there (and also in header)
        rowGroupPanelShow: 'always',
        disableAllRowGrouping: false,  // if true, we have to set enableRowGroup and enablePivot to false in colDefs
    },
    header: {  // allows grouping by clicking on the col header and picking that option
        rowGroupPanelShow: 'onlyWhenGrouping',
        disableAllRowGrouping: false,
    },
    disabled: {  // no grouping allowed
        rowGroupPanelShow: 'never',  // TODO - also need to set enableRowGroup and enablePivot to false in colDefs
        disableAllRowGrouping: true,
    },
}

const ACTIONS_COLUMN_KEY = '_actions_column';


export default {
    name: 'ObjectGrid',
    components: {
        AgGridVue,
        PrimeCascadeSelect,
        PrimeButton,
        PrimeDropdown,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    i18n: {  // NOTE: we need to make sure we inject VueI18n instance to the Vue instance to use this... needs work
        messages: {
            en: {
                objectGrid: {
                    quickFilters: 'Quick Filters',
                    presets: 'Presets',
                    viewAll: 'View All',
                    filtersApplied: 'Filters Applied',
                    resetFiltersGrouping: 'Reset Filters / Grouping',
                    refresh: 'Refresh',
                    resetColumns: 'Reset Columns',
                    autoSizeColumns: 'Auto-Size Columns',
                    exportToExcel: 'Export to Excel',
                    emptyText: 'No data to show',
                    createdTimeFilter: {
                        label: 'Created Time',
                        placeholder: 'Select...',
                        options: {
                            last_24_hours: 'Last 24 Hours',
                            last_48_hours: 'Last 48 Hours',
                            last_7_days: 'Last 7 Days',
                            last_30_days: 'Last 30 Days',
                            last_6_months: 'Last 6 Months',
                            last_1_year: 'Last 1 Year',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: 'My Involvement',
                        placeholder: 'Select...',
                        options: {
                            'true': 'Involved',
                            'false': 'Not Involved',
                        }
                    }
                },
            },
            es: {  // es-MX and es-ES will use this same translation for now (seems safe)
                objectGrid: {
                    quickFilters: 'Filtros Rápidos',
                    presets: 'Preajustes',
                    viewAll: 'Ver Todo',
                    filtersApplied: 'Filtros Aplicados',
                    resetFiltersGrouping: 'Restablecer Filtros / Agrupación',
                    refresh: 'Actualizar',
                    resetColumns: 'Restablecer Columnas',
                    autoSizeColumns: 'Autoajustar Columnas',
                    exportToExcel: 'Exportar a Excel',
                    emptyText: 'No hay datos para mostrar',
                    createdTimeFilter: {
                        label: 'Tiempo de Creación',
                        placeholder: 'Seleccionar...',
                        options: {
                            last_24_hours: 'Últimas 24 Horas',
                            last_48_hours: 'Últimas 48 Horas',
                            last_7_days: 'Últimos 7 Días',
                            last_30_days: 'Últimos 30 Días',
                            last_6_months: 'Últimos 6 Meses',
                            last_1_year: 'Último Año',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: 'Mi Participación',
                        placeholder: 'Seleccionar...',
                        options: {
                            'true': 'Involucrado',
                            'false': 'No Involucrado',
                        }
                    },
                },
            },
            pt: {  // pt-BR and pt-PT seem to be the same for these
                objectGrid: {
                    quickFilters: 'Filtros Rápidos',
                    presets: 'Predefinições',
                    viewAll: 'Ver Todos',
                    filtersApplied: 'Filtros Aplicados',
                    resetFiltersGrouping: 'Redefinir Filtros / Agrupamento',
                    refresh: 'Atualizar',
                    resetColumns: 'Redefinir Colunas',
                    autoSizeColumns: 'Ajustar Automaticamente o Tamanho das Colunas',
                    exportToExcel: 'Exportar para Excel',
                    emptyText: 'Nenhum dado para exibir',
                    createdTimeFilter: {
                        label: 'Tempo de Criação',
                        placeholder: 'Selecionar...',
                        options: {
                            last_24_hours: 'Últimas 24 Horas',
                            last_48_hours: 'Últimas 48 Horas',
                            last_7_days: 'Últimos 7 Dias',
                            last_30_days: 'Últimos 30 Dias',
                            last_6_months: 'Últimos 6 Meses',
                            last_1_year: 'Último Ano',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: 'Meu Envolvimento',
                        placeholder: 'Selecionar...',
                        options: {
                            'true': 'Envolvido',
                            'false': 'Não Envolvido',
                        }
                    },
                },
            },
            zh: {
                objectGrid: {
                    quickFilters: '快速筛选',
                    presets: '预设',
                    viewAll: '查看全部',
                    filtersApplied: '已应用筛选器',
                    resetFiltersGrouping: '重置筛选器/分组',
                    refresh: '刷新',
                    resetColumns: '重置列',
                    autoSizeColumns: '自动调整列大小',
                    exportToExcel: '导出到 Excel',
                    emptyText: '没有数据可显示',
                    createdTimeFilter: {
                        label: '创建时间',
                        placeholder: '选择...',
                        options: {
                            last_24_hours: '过去 24 小时',
                            last_48_hours: '过去 48 小时',
                            last_7_days: '过去 7 天',
                            last_30_days: '过去 30 天',
                            last_6_months: '过去 6 个月',
                            last_1_year: '过去 1 年',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: '我的参与',
                        placeholder: '选择...',
                        options: {
                            'true': '参与',
                            'false': '不参与',
                        }
                    },
                },
            },
            'zh-Hans': {
                objectGrid: {
                    quickFilters: '快速筛选',
                    presets: '预设',
                    viewAll: '查看全部',
                    filtersApplied: '已应用筛选器',
                    resetFiltersGrouping: '重置筛选器/分组',
                    refresh: '刷新',
                    resetColumns: '重置列',
                    autoSizeColumns: '自动调整列大小',
                    exportToExcel: '导出到 Excel',
                    emptyText: '没有数据可显示',
                    createdTimeFilter: {
                        label: '创建时间',
                        placeholder: '选择...',
                        options: {
                            last_24_hours: '过去 24 小时',
                            last_48_hours: '过去 48 小时',
                            last_7_days: '过去 7 天',
                            last_30_days: '过去 30 天',
                            last_6_months: '过去 6 个月',
                            last_1_year: '过去 1 年',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: '我的参与',
                        placeholder: '选择...',
                        options: {
                            'true': '参与',
                            'false': '不参与',
                        }
                    },
                },
            },
            'zh-Hant': {
                objectGrid: {
                    quickFilters: '快速篩選',
                    presets: '預設',
                    viewAll: '查看全部',
                    filtersApplied: '已應用篩選器',
                    resetFiltersGrouping: '重置篩選器/分組',
                    refresh: '刷新',
                    resetColumns: '重置列',
                    autoSizeColumns: '自動調整列大小',
                    exportToExcel: '導出到 Excel',
                    emptyText: '沒有數據可顯示',
                    createdTimeFilter: {
                        label: '創建時間',
                        placeholder: '選擇...',
                        options: {
                            last_24_hours: '過去 24 小時',
                            last_48_hours: '過去 48 小時',
                            last_7_days: '過去 7 天',
                            last_30_days: '過去 30 天',
                            last_6_months: '過去 6 個月',
                            last_1_year: '過去 1 年',
                        }
                    },
                    isTuInvolvedFilter: {
                        label: '我的參與',
                        placeholder: '選擇...',
                        options: {
                            'true': '參與',
                            'false': '不參與',
                        }
                    },
                },
            },
        },
    },
    props: {
        modelClass: {
            type: String,
            required: true,
        },
        flowSubtype: {  // use this if you are working with a flow object model class that supports flow subtypes
            type: String,
            default: null,
        },
        fixedFilterModel: {  // fixed filter model applied on every load, but grid will show that its filtered - may want to use fixedFilterKwargs instead, can be used together, but probably no need
            type: Object,
            required: false,
        },
        fixedFilterKwargs: {  // passed directly to backend queryset filter method -- filters before any other filters are applied
            type: Object,
            required: false,
        },
        fixedSortModel: {
            type: Array,
            required: false,
        },
        includeFields: {  // ONLY ONE OF includeFields OR excludeFields SHOULD BE USED
            type: Array,
            required: false,
        },
        excludeFields: {  // ONLY ONE OF includeFields OR excludeFields SHOULD BE USED
            type: Array,
            required: false,
        },
        gridMaxHeight: {
            type: String,
            required: false,
            default: '100%',
        },
        emptyText: {
            type: String,
            required: false,
            default: 'No items',
        },
        loadingText: {
            type: String,
            required: false,
            default: 'Loading...',
        },
        blockSize: {
            type: Number,
            required: false,
            default: 50,
        },
        rowGrouping: {  // affects rowGroupPanelShow and overall ability to group rows - NOT REACTIVE, just used once
            type: String,
            required: false,
            default: 'panel',  // options are 'panel', 'header', 'disabled' -- see rowGroupingParams 
        },
        disableColStateLocalStorage: {  // OPTIONAL - if set, it complete disables saving column state to local storage
            type: Boolean,
            required: false,
            default: false,  // default false so we can just use the presence of attr to disable
        },
        colStateLocalStorageKey: {  // OPTIONAL - if set, uses this (as part of) the localStorage key for column state (so you can have multiple object grids for same model with separate keys in local storage) - if not provided, then its just not part of the key and any object grid on same tenant will share the same state in local storage
            type: String,
            required: false,
            default: null,
        },
        // FLAGS TO SHOW/HIDE VARIOUS ACCESSORIES
        hidePresets: {
            type: Boolean,
            default: false,
        },
        // hide created filter / TU involved filter?  those are generic extra filters.... which should maybe be a prop anyways, so either make that a prop, or make them fixed and have fixed props to hide them
        rowActions: {  // Array of actions, OR function that takes ag-grid ICellRendererParams and returns array of action objects for ActionCellRenderer
            type: [Function, Array],   // should be or return array of action objects with { key: string, label: string, bsIcon: string, variant: string }
            required: false,  // if you pass in something here, then you should also have event handler for @action-click, which takes (actionKey, )
            default: null,
        },
    },
    data() {
        return {
            providerReady: false,
            gridProvider: null,  // set this in prepareProvider()
            colDefs: null,  // get from gridProvider when its ready
            initialColumnStateParams: null,  // null (not set, ignored) or Object (set from localStorage, if we have it)
            skipNextColResizeBecauseWeJustSetInitialColState: false,  // :)  set to true when we set initial column state, so we don't resize cols
            loadingRows: false,  // USED IN CHILD COMPONENTS, DO NOT CHANGE THIS NAME
            firstRowsLoaded: false,
            gridLoadedEmpty: false,
            lastDataLoadedAt: null,  // set to Date.now() when we load data (timestamp number), then you can monitor when things actually reload 
            dataLoadedSinceColAutosize: false,  // set false to re-autosize cols when data loads, set true when data loads 
            shouldAutoSizeColumnsWhenModelUpdated: true,  // set this false after first load, and after you reset columns (by setting grouping, etc.) but NOT when just loading additional data 
            gridOptions: null,  // set in beforeMount()
            gridApi: null,  // set in onGridReady()
            gridColumnApi: null,  // set in onGridReady()
            sideBar: null,
            defaultColDef: null,
            columnTypes: null,
            gridContext: null,
            autoGroupColumnDef: null,
            rowGroupPanelShow: null,
            enableRangeSelection: true, // enable range selection, so we can select multiple rows and copy them to clipboard?
            allowClientSideExcelExport: false,  // allowServerSideExcelExport is a computed, based on gridProvider
            // cacheBlockSize: 50,  // how many rows to load at a time - changed to prop blockSize via localBlockSize
            blockLoadDebounceMillis: 200,  // set this to avoid loading every single block as user scrolls down page, and to avoid 2 requests when setting a preset that has both filter and grouping
            suppressContextMenu: true,  // off so people can right-click on title col to open things in new tab, and to disable export to Excel because full data is not loaded (necessarily)
            gridLocaleText: null,  // set in beforeMount() -- entire object of localeText for the current user's locale/language
            // PRESETS
            gridPresets: [],  // preset filter objects 
            gridPresetSelected: null,  // selected preset filter object
            lastSetFiltersGrouping: {
                filters: null,
                grouping: [],
            },  // object with { filters, grouping } - for change detection
            lastSetFiltersGroupingStringified: '',  // updated when we select a preset
            currentFilters: {},  // make sure always an object..
            currentGroupingCols: [],
            gridStats: {},  // holder of various info that we update when grid changes
            // EXTRA FILTERS / FIXED FILTERS
            extraFilters: [],  // populated in created() with translated labels
            extraFilterRegistry: {},
            extraFilterValueKeys: {},  // just the 'key' of selected option 
            fixedFilterFields: [],  // update this based on any fields in fixedFilterModel, then disable filtering (and grouping/sorting?) on those cols
            // EXCEL DOWNLOAD STUFF
        }
    },
    computed: {
        finalEmptyText() {
            return this.$t('objectGrid.emptyText') || this.emptyText;
        },
        emptySlotProps() {
            return {
                emptyText: this.finalEmptyText,
            };
        },
        loadingSlotProps() {
            return {
                loadingText: this.loadingText,
                providerReady: this.providerReady,
                loadingRows: this.loadingRows,
            };
        },
        anyRowsLoading() {
            // true if first rows are not loaded yet, or if we are currently loading rows
            return this.loadingRows || !this.firstRowsLoaded;
        },
        lastSetPresetIsCurrent() {
            //return this.lastSetFiltersGroupingStringified ? 
            return isEmpty(this.lastSetFiltersGrouping) ? true : isEqual(this.lastSetFiltersGrouping, {filters: this.currentFilters, grouping: this.currentGroupingCols});
        },
        gridHasAnyFilters() {  // GRID - not external
            return !isEmpty(this.currentFilters);
        },
        gridHasAnyGrouping() {
            return !isEmpty(this.currentGroupingCols);
        },
        hasAnyFiltersOrGrouping() {
            return !!this.gridHasAnyFilters || !!this.gridHasAnyGrouping;
        },
        isReset() {
            return !this.gridHasAnyFilters && !this.gridHasAnyGrouping; // && !this.hasAnyExternalFiltersApplied;
        },
        totalItemCount() {  // logically can only be known by backend -- used in child components, so do not change this name
            return this.gridProvider?.lastTotalItemCount ?? 0;
        },
        filteredItemCount() {  // logically can only be known by backend -- used in child components, so do not change this name
            return this.gridProvider?.lastFilteredItemCount ?? 0;
        },
        allColKeys() {
            return this.colDefs ? this.colDefs.map(col => col.field) : [];
        },
        registeredExtraFilters() {
            // get Array of all extraFilters that were actually registered
            // set ordering?
            return Object.values(this.extraFilterRegistry);
        },
        visibleExtraFilters() {
            // filtered array of filters in extraFilterRegistry that are ACTUALLY present in the columns - updates based on used cols 
            return Object.values(this.extraFilterRegistry).filter(flt => this.allColKeys.includes(flt.field));
        },
        localBlockSize() {
            // use prop blockSize if set, coerced to number, or fallback to default 
            const defaultBlockSize = 50;
            return this.blockSize ? (toInteger(this.blockSize) || defaultBlockSize) : defaultBlockSize;
        },
        finalColStateLocalStorageKey() {
            // use prop colStateLocalStorageKey if set, return empty string if not supposed to save colState
            const disabledKey = '';  // return this when we're not supposed to save colState
            if (this.disableColStateLocalStorage) {
                return disabledKey;
            }
            const keyFromProp = this.colStateLocalStorageKey;  // might be '' or null or undefined - if so, we just don't use it in the key
            const hostname = window?.location?.hostname;  // will be just hostname, no protocol or port
            const modelSubtypePrefix = this.flowSubtype ? `${this.modelClass}-${this.flowSubtype}` : this.modelClass;
            const colStateConstant = 'colState';  // so we can choose to save other things later
            const validKeyParts = [hostname, 'ObjectGrid', modelSubtypePrefix, colStateConstant, keyFromProp].filter(p => !!p);
            return validKeyParts.length ? validKeyParts.join('-') : disabledKey;
        },
        allowServerSideExcelExport() {
            // NOTE - we also have many things that put it in disabled state (button)
            // LETS GO! const allowedFeatureFlag = this.$currentUserInfo.user_is_staff && this.$currentUserInfo.is_tenant_admin;
            const providerAllows = this.gridProvider?.allowExcelDownload || false;
            return providerAllows;   // && allowedFeatureFlag;
        },
        isLoadingExcel() {
            return this.gridProvider?.isLoadingExcel;  // proxy the flag on grid provider so we can modify things here
        },
        hasExcelError() {
            return !!this.gridProvider?.loadExcelError;
        },

    },
    created() {
        // BUILD the extra filters -- we do it this way so we can use the $t() translation function to get the labels for the filters
        this.extraFilters = [
            getCreatedTimeframeFilter({
                label: this.$t('objectGrid.createdTimeFilter.label'),
                placeholder: this.$t('objectGrid.createdTimeFilter.placeholder'),
                options: [
                    { key: 'last_24_hours', label: this.$t('objectGrid.createdTimeFilter.options.last_24_hours'), filterParams: { type: 'greaterThan', dateFromDuration: {hours: 24}, dateFromDirection: 'past' } },
                    { key: 'last_48_hours', label: this.$t('objectGrid.createdTimeFilter.options.last_48_hours'), filterParams: { type: 'greaterThan', dateFromDuration: {hours: 48}, dateFromDirection: 'past' } },
                    { key: 'last_7_days', label: this.$t('objectGrid.createdTimeFilter.options.last_7_days'), filterParams: { type: 'greaterThan', dateFromDuration: {days: 7}, dateFromDirection: 'past' } },
                    { key: 'last_30_days', label: this.$t('objectGrid.createdTimeFilter.options.last_30_days'), filterParams: { type: 'greaterThan', dateFromDuration: {days: 30}, dateFromDirection: 'past' } },
                    { key: 'last_6_months', label: this.$t('objectGrid.createdTimeFilter.options.last_6_months'), filterParams: { type: 'greaterThan', dateFromDuration: {months: 6}, dateFromDirection: 'past' } },
                    { key: 'last_1_year', label: this.$t('objectGrid.createdTimeFilter.options.last_1_year'), filterParams: { type: 'greaterThan', dateFromDuration: {years: 1}, dateFromDirection: 'past' } },
                ],
            }),
            getIsTuInvolvedFilter({
                label: this.$t('objectGrid.isTuInvolvedFilter.label'),
                placeholder: this.$t('objectGrid.isTuInvolvedFilter.placeholder'),
                options: [
                    { key: 'true', label: this.$t('objectGrid.isTuInvolvedFilter.options.true') },
                    { key: 'false', label: this.$t('objectGrid.isTuInvolvedFilter.options.false') },
                ],
            }),
        ];
        // register extra filters, set their value in data above 
        this.fixedFilterFields = Object.keys(this.fixedFilterModel ?? {});
        this.extraFilters.forEach(flt => {
            // NOTE - we register them here, but they may not actually be used in the grid, depending on what model class we are showing
            if (!this.fixedFilterFields.includes(flt.field)) {
                this.$set(this.extraFilterRegistry, flt.field, flt);
                this.$set(this.extraFilterValueKeys, flt.field, flt.emptyValue);
            }
        });
        // GET STICKY STUFF FROM LOCAL STORAGE IF CONFIGURED - merge with other things set in props before proceeding!
        // setup our debounced save function also
        this.debouncedSaveCurrentColumnStateToLocalStorage = debounce(this.saveCurrentColumnStateToLocalStorage, 500);  // debounce to avoid saving too often
        // this is only honored once - on first load
        const localStorageColumnStateParams = this.getColumnStateParamsFromLocalStorage();  // returns null if not set
        if (localStorageColumnStateParams) {
            // we stash it on data, then use it later once the grid is alive/ready
            // or should we set it anyways?  this is created hook, so its already null....
            this.initialColumnStateParams = localStorageColumnStateParams;
            this.skipNextColResizeBecauseWeJustSetInitialColState = true;  // set this so we don't resize cols when we set initial state
        }
    },
    beforeMount() {
        // setup localeText -- but machinery might not be working in all places yet, so we make it very defensive
        try {
            const currentLocale = this.$splLocale;  // this comes from the global mixin
            if (!currentLocale) {
                console.warn('No current locale found, defaulting to English for ag-grid locale text');
                this.gridLocaleText = null;  // this will make ag-grid use its default locale text (English)
            } else {
                this.gridLocaleText = getAgGridLocaleText(currentLocale);  // use the currentLocale variable we just defined
            }
        } catch (err) {
            console.warn('Error getting ag-grid locale text, possibly because all i18n machinery is not implemented yet', err);
            this.gridLocaleText = null;  // this will make ag-grid use its default locale text (English)
        }
        this.gridOptions = {
            components: {
            },
            frameworkComponents: {
                smStateRenderer: SmStateRenderer,
                ssrmStatusBarComponent: SsrmStatusBarComponent,
                progressBarComponent: ProgressBarComponent,
                progressStepComponent: ProgressStepComponent,
                emptyGridOverlayComponent: EmptyGridOverlayComponent,
                bootstrapPhoneNumberRenderer: BootstrapPhoneNumberRenderer,
                prefetchedOverdueNodesRenderer: PrefetchedOverdueNodesRenderer,
                prefetchedActiveNodesRenderer: PrefetchedActiveNodesRenderer,
                companyTagListRenderer: CompanyTagListRenderer,
                companyTagSingleRenderer: CompanyTagSingleRenderer,
                companySiteTypeRenderer: CompanySiteTypeRenderer,
                splTagListRenderer: SplTagListRenderer,
                splTagSingleRenderer: SplTagSingleRenderer,
                rfxListRenderer: RfxListRenderer,
                contactFullNameRenderer: ContactFullNameRenderer,
                portalAccessBadgeRenderer: PortalAccessBadgeRenderer,
                emailAddressRenderer: EmailAddressRenderer,
                defaultContactRenderer: DefaultContactRenderer,
                tenantRolesRenderer: TenantRolesRenderer,
                compactDateTimeRenderer: CompactDateTimeRenderer,
                actionCellRenderer: ActionCellRenderer,
            },
            noRowsOverlayComponent: 'emptyGridOverlayComponent',
            noRowsOverlayComponentParams: {
                message: 'No data to show',
                componentParent: this,
            },
            getRowNodeId: function(data) {  // !IMPORTANT - you have custom components that rely on node IDs being the object IDs!
                return data ? data.id : '';  // should return strings
            },  // use this to have grid use your own defined node IDs... or let it use its own.  should return strings.
            popupParent: document.querySelector('body'),  // needed so popups overflow outside the grid
            groupUseEntireRow: true,  // makes the group rows take up entire row, not just one column (if true), but then there is no col either
            groupRowRenderer: 'agGroupCellRenderer',
            groupRowRendererParams: {
                innerRenderer: params => {
                    //console.log('groupRowRenderer innerRenderer params', params);
                    return `<span class="font-weight-bold">${ params.valueFormatted || params.data.label || '(None)' }</span>`;  // overriding innerRenderer so we still show expander arrows and counts, label is just a fallback if valueFormatted is null (valueFormatted as the value from any lookup fields)
                },
            },
            //showOpenedGroup: true,
            getChildCount: (data) => {
                // need this to show child count in group row
                return data.count;
            },
            tooltipShowDelay: 500,  // delay before showing tooltips -- default is 2000 which is like forever
        };
        this.statusBar = {  // SHOULD ONLY BE USED WITH CLIENT SIDE ROW MODEL
            statusPanels: [
                {
                    statusPanel: 'ssrmStatusBarComponent',
                    align: 'left',
                },
                //{ statusPanel: 'agFilteredRowCountComponent' },
            ]
        };
        this.autoGroupColumnDef = {  // not used? if you use groupUseEntireRow: true
            // minWidth: 200,  // 
            //headerName: 'Group',
            //field: 'title',  // NOTE - this is then fixed to QualityIssue - or other things with 'title' as the main col
            cellRenderer: 'agGroupCellRenderer',
            /* cellRendererParams: {
                innerRenderer: params => {
                    console.log('innerRenderer params', params);
                    return params.valueFormatted ?? params.data.label;  // overriding innerRenderer so we still show expander arrows and counts, label is just a fallback if valueFormatted is null (valueFormatted as the value from any lookup fields)
                },
            }, */
            //cellRenderer: params => (`HELLO: ${params.value}`),
        };
        this.rowGroupPanelShow = rowGroupingParams[this.rowGrouping]?.rowGroupPanelShow || 'always';   // 'always'; // options are 'always', 'never', 'onlyWhenGrouping' - get from 'rowGrouping' prop instead
        this.gridContext = {
            componentParent: this,
        };
        this.sideBar = {
            position: 'left',  // left, right
            hiddenByDefault: false,
            defaultToolPanel: null,  // if null, then it starts closed -- or 'columns' or 'filters'
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    toolPanelParams: {  // may want all these off... but experimenting with them on for now
                        suppressRowGroups: true,  // show in top bar instead
                        suppressValues: true,  // for aggregation... but we don't really have aggregatable values
                        suppressPivots: true,
                        suppressPivotMode: true,
                        suppressColumnFilter: false,
                        suppressColumnSelectAll: false,
                        suppressColumnExpandAll: false,
                        contractColumnSelection: false,  // if true, then groups start contracted instead of expanded - maybe make this dynamic based on category?
                    },
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                },
            ],
        };
        this.defaultColDef = {
            resizable: true,
            editable: false,
            sortable: true,
            filter: true,
            floatingFilter: true,  // not really needed.... not many text fields, just set filters
            //suppressMovable: true,
            suppressMenu: false,  // hides the menu for each col... set false if you want filters, or use inline/floating? filters
            menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],  // shown in order 'columnsMenuTab'
            wrapText: false,  // easier for autoSize
            autoHeight: false,  // not allowed if doing row spanning - turn off if wrapText is false - might be a big performance hit!
            singleClickEdit: false,
            enableCellChangeFlash: true,
            chartDataType: 'category',  // most should be this - if series, set those specifically in their type
            //enableRowGroup: true,  // allows it to be grouped via tool panel - only do it for certain data types
            //enablePivot: true,  // allows it to be pivot enabled via tool panel
            valueGetter: (params) => {
                // params.colDef.field is our dotted path 
                return get(params.data, params.colDef.field);  // use this instead of expression in case a row does not have that nested value.... lodash get has built-in optional chaining basically 
            },
        };
        this.columnTypes = columnTypes;
    },
    async mounted() {
        await this.prepareProvider();
    },
    methods: {
        // DATA LOADING
        setLoadingRows(isLoading) {
            // pass this to gridProvider so it can set loadingRows
            console.log('setting loadingRows', isLoading);
            this.loadingRows = isLoading;  // need to set this before autoSizeColsIfNeeded() so it knows not to autosize
            if (!isLoading) {
                // if in here, then we just FINISHED loading
                if (!this.firstRowsLoaded) {
                    console.log('FIRST ROWS LOADED');
                    this.firstRowsLoaded = true;
                }
                //this.autoSizeColsIfNeeded();  // if we are setting to NOT loading, then we just loaded new data, check if we should autoSize
                //this.afterDataLoaded();
                this.lastDataLoadedAt = Date.now();
                this.dataLoadedSinceColAutosize = true;  // set this so we know to autosize again when data loads - can always set to true 
            }
            //this.autoSizeColsIfNeeded();
        },
        async prepareProvider() {
            this.gridProvider = new GridProvider({
                model_class: this.modelClass,
                flowSubtype: this.flowSubtype,
                loadingRowsCallback: this.setLoadingRows,
                filterModel: this.fixedFilterModel,
                sortModel: this.fixedSortModel,
                includeFields: this.includeFields,
                excludeFields: this.excludeFields,
                fixedFilterKwargs: this.fixedFilterKwargs,
            });
            return this.gridProvider.load().then(() => {
                // GET ALL THINGS FROM GRID PROVIDER - colDefs, context, presets, etc.
                this.gridContext = Object.assign({}, this.gridContext, this.gridProvider.gridContext);  // MERGE in gridContext, so we don't remove things we already put there 
                const rawColDefs = this.gridProvider.getAgGridColDefs();
                // SET ANY OVERRIDES BASED ON STATE HERE -- TODO
                const disableAllRowGrouping = rowGroupingParams[this.rowGrouping]?.disableAllRowGrouping ?? false;
                const preppedColDefs = rawColDefs.map(col => {
                    // do any overrides here
                    if (disableAllRowGrouping) {
                        col.enableRowGroup = false;
                        col.enablePivot = false;
                    }
                    // we will NOT set the order / visibility in here... do that in computed so we can make it reactive based on any saved col state in local storage
                    return col;
                });
                const actionColDef = this.getActionColDef();
                if (actionColDef) {
                    preppedColDefs.push(actionColDef);  // TODO - might have to ignore this column when saving/restoring col state, or when doing other things?
                }
                // NOTE - you MAY have to merge the initialColumnStateParams in here, if you want to apply it before grid is ready
                // but that is column STATE, not colDefs.... they are not exactly the same thing... so don't do that.
                this.colDefs = preppedColDefs;
                console.log('got colDefs from gridProvider', this.colDefs);
                console.log('colDefs in order:', this.colDefs.map(col => col.field));
                this.gridPresets = this.gridProvider.gridPresets;
                this.providerReady = true;
                //this.loadInitialData();  // no await here... just trigger and go
            }).catch((error) => {
                this.apiErrorHandler(error);
            });
        },
        // ACTION COLUMN 
        hasActionColumn() {
            // called super early to see if we need to add action column, and add it to column state if it was not there before we last saved col state
            return this.getActionColDef() !== null;
        },
        getActionColDef() {
            // build the colDef for the action column if it is enabled/configured
            // should this just be a colType??? then we could have multiple (but probably not needed) - maybe other benefits
            const rawProp = this.rowActions;
            // if it is not a function or array, return null so we don't even show it
            // there is other validation in ActionCellRenderer
            if (Array.isArray(rawProp) || typeof rawProp === 'function') {
                // if we have empty array, still show the col so it can be populated later?  in case we have dynamic prop value?
                const actionColDef = {
                    headerName: 'Actions',
                    field: ACTIONS_COLUMN_KEY,  // watch out for any conflicts with actual data fields?
                    // type: 'actionColDefType',
                    headerClass: 'object-grid-action-header-cell',
                    cellClass: 'object-grid-action-cell',
                    // cellStyle: {
                    //     paddingLeft: '4px',
                    //     paddingRight: '4px',
                    // },
                    
                    //suppressMovable: true,
                    headerTooltip: 'Additional Actions',
                    tooltipValueGetter: () => 'Additional Actions',  // you have full set of params available if needed
                    cellRenderer: 'actionCellRenderer',
                    cellRendererParams: {
                        actions: Array.isArray(rawProp) ? rawProp : undefined,  // different params for function vs array - handled in ActionCellRenderer
                        getActions: typeof rawProp === 'function' ? rawProp : undefined,
                        componentParent: this,
                    },
                    suppressSizeToFit: true,  // so it doesn't get resized with sizeColsToFit()
                    suppressColumnsToolPanel: true,  // hide from columns tool panel
                    suppressFiltersToolPanel: true,  // hide from filters tool panel (will be hidden anyways because no filter)
                    suppressMenu: true,
                    suppressMovable: true,
                    width: 70,
                    initialWidth: 70,
                    minWidth: 70,
                    maxWidth: 100,
                    resizable: false,  // only turn this off if you are confident in your custom component
                    editable: false,
                    sortable: false,
                    filter: false,
                    pinned: 'right', // pin to RIGHT side so it always shows up even in wide grids
                    // lockPinned: true, // prevent unpinning
                };
                return actionColDef;
            }
            return null;
        },
        handleActionClick(actionObj, actionCellParams) {
            // called from the actionCellRenderer when an action is clicked
            // actionObj is the action object that was clicked -- pass the entire thing back up
            // actionCellParams is the ag-grid ICellRendererParams for that action cell { data, node, colDef, etc. }
            // for the actionObj, we will just pass the entire thing back up -- should have all the same stuff as what was passed in (but likely a new object reference)
            // for the actionCellParams, we will strip off some of the stuff from actionCellParams before emitting, because there are a lot of ag-grid specific things in there that should not make their way up to parent
            // should we add in anything else from here???  context?
            // what about the parent doing things that modify data, need row refresh?  
            // TODO: we need a refreshRow function on here, and as standard endpoint for object grid!
            const rowData = actionCellParams.data;  // maybe also:  node, rowIndex, context? (grid context)
            this.$emit('action-click', actionObj, rowData);
        },

        // GRID ACTIONS
        onGridReady(params) {
            console.log('GRID READY from onGridReady()', params);
            this.gridApi = this.gridApi ?? params.api;
            this.gridColumnApi = this.gridColumnApi ?? params.columnApi;
            // APPLY THE SAVED COLUMN STATE HERE -- would be ideal to do it earlier... but that causes strangeness in the grid loading for some reason... shows Loading.. first.
            if (this.initialColumnStateParams) {
                // put this as early as possible...
                //console.log('**** apply initialColumnStateParams here?');
                this.applySavedColumnStateParamsToGrid(this.initialColumnStateParams);
            }
        },
        onGridModelUpdated(params) {
            console.log('onGridModelUpdated', params);
            //const gridApi = params.api;
            //const serverSideRowState = gridApi.getServerSideStoreState();
            //console.log('serverSideRowState', serverSideRowState);
            //console.log('getDisplayedRowCount', gridApi.getDisplayedRowCount());
            this.updateLastSetFilters();  // update our lastSetFiltersGrouping -- need to wait until here so grid is updated 
            if (this.filteredItemCount == 0 && !this.loadingRows) {
                this.showNoRowsOverlay();
            } else {
                this.hideOverlay();
            }
            this.updateGridStats();
            if (this.dataLoadedSinceColAutosize) {
                this.autoSizeColsIfNeeded(); // only do this once data has loaded since we last autosized
            }
        },
        onRowDataChanged(params) {  // called BEFORE onGridModelUpdated
            console.log('onRowDataChanged', params);
        },
        updateGridStats() {
            // update entire this.gridStats object -- mainly for DEBUG
            this.gridStats = {
                displayedRowCount: this.gridApi?.getDisplayedRowCount() ?? 0,
                //infiniteRowCount: this.gridApi?.getInfiniteRowCount() ?? 0,
                //isLastRowIndexKnown: this.gridApi?.isLastRowIndexKnown(),
                providerLastTopLevelCount: this.gridProvider?.lastTopLevelRowCount,
                providerLastTotalItemCount: this.gridProvider?.lastTotalItemCount,
                providerLastFilteredItemCount: this.gridProvider?.lastFilteredItemCount,
            };
        },
        onFirstDataRendered(params) {
            // initial rows won't actually be loaded here....
            console.log('onFirstDataRendered', params);
            this.updateLastSetFilters();
        },
        onRowGroupOpened(params) {
            console.log('onRowGroupOpened', params);
            this.shouldAutoSizeColumnsWhenModelUpdated = true;  // toggle on so it autoSizes when data loads 
            this.dataLoadedSinceColAutosize = false;  // reset this so we know to autosize again when data loads
        },
        sizeColsToFit() {
            // this fits it to the grid width, if lots of cols, it looks horrible - see below, you will only use this when cols are not covering the entire grid width
            this.gridApi?.sizeColumnsToFit();  // fits to grid width -- pass a number to set specific width
            this.handleAnyColumnStateChange();
        },
        smartColResize() {
            // resize columns in a smart way, because you are smart!  if cols are smaller than available space, we size to fit, otherwise we autoSize
            // NOTE: this isn't that smart... it doesn't take into account the content in each col, so cols will be even widths, which looks strange
            const allDisplayedCols = this.gridColumnApi?.getAllDisplayedColumns() ?? [];
            const lastCol = allDisplayedCols.length ? allDisplayedCols[allDisplayedCols.length - 1] : null;
            const lastColRight = lastCol ? lastCol.left + lastCol.actualWidth : null;
            const gridWidth = this.$refs.theGrid?.$el?.offsetWidth ?? null;
            const sidebarWidth = 30;  // just hardcode for now  // sidebar is 30px
            const availableWidth = gridWidth ? gridWidth - sidebarWidth : null;
            if (availableWidth && lastColRight && lastColRight < availableWidth) {
                this.sizeColsToFit();  // will expand cols to fill the grid width
            } else {
                this.autoSizeAll();  // will shrink cols to fit content, but not squeeze them to force fit in grid
            }
        },
        autoSizeAll() {
            // this fits it to the content, but can be slow if lots of rows - will not squeeze everything into grid width though... but will not fill grid if we only have a few cols - use smartColResize() for that
            const skipHeader = true;
            if (this.gridColumnApi) {
                this.gridColumnApi.autoSizeAllColumns(null, skipHeader);  // there are other methods for only autoSize one or multiple
            }
        },
        autoSizeColsIfNeeded() {
            // only autoSize if we have not already done it, if first data has loaded, and if not currently loading 
            // AND if we didn't just set initial column state (because that will resize them all anyways)
            //console.log('AUTO SIZE COLS IF NEEDED', this.shouldAutoSizeColumnsWhenModelUpdated, this.firstRowsLoaded, !this.loadingRows);
            if (this.shouldAutoSizeColumnsWhenModelUpdated && this.firstRowsLoaded && !this.loadingRows) {
                this.shouldAutoSizeColumnsWhenModelUpdated = false;  // toggle back off 
                if (this.skipNextColResizeBecauseWeJustSetInitialColState) {
                    // skip it, but toggle back off
                    console.log('skipNextColResizeBecauseWeJustSetInitialColState');
                    this.skipNextColResizeBecauseWeJustSetInitialColState = false;  // toggle back off
                    return;
                }
                this.autoSizeAll();
            }
        },
        onColumnVisible() {
            console.log('onColumnVisible');
            this.handleAnyColumnStateChange();
        },
        onColumnMoved(params) {
            console.log('onColumnMoved', params);
            if (params.dragStopped) {
                this.handleAnyColumnStateChange();
            }
        },
        onColumnResized(params) {
            // called when a column is resized by the user
            // console.log('onColumnResized', params);
            //this.gridApi.resetRowHeights();   // TODO - need to change this for SSRM, not the same as client-side
            const isDone = !!params.finished;
            if (isDone) {
                this.handleAnyColumnStateChange();
            }
        },
        onGridColumnsChanged() {  // doesn't do much... only when original list changes?
            console.log('onGridColumnsChanged');
            this.handleAnyColumnStateChange();
        },
        onDisplayedColumnsChanged() {
            console.log('onDisplayedColumnsChanged');
            this.handleAnyColumnStateChange();
        },
        handleFilterChanged(params) {  // can take event as arg, if you need it
            // callback that handles the grid filterChanged event - need to compare with our own 
            // need to make sure that if user manually changes things we filter via api, that the dropdown selections change? that will be hard...  
            // USE THIS FOR CHANGE DETECTION
            console.log('handleFilterChanged', params);
            const currentFilters = this.gridApi.getFilterModel();  // returns {} if no filters
            //console.log('currentFilters', currentFilters, JSON.stringify(currentFilters));
            this.currentFilters = Object.assign({}, cloneDeep(currentFilters)); 
            //console.log('setting currentFilters', JSON.stringify(currentFilters));
            // don't call .updateLastSetFilters() here, new filters will not be 'ready' in the grid yet -- call when grid model changed 
            this.handleAnyColumnStateChange();
        },
        handleColumnRowGroupChanged(event) {   
            // callback from the grid, when any row grouping is changed - need to check, because maybe our other method triggered it
            //console.log('columnRowGroupChanged', event);
            // if event.source === 'api' that was us doing it from here, not the user
            const groupedColIds = event.columns.length ? event.columns.map(c => c.colId) : [];
            this.currentGroupingCols = [...groupedColIds];
            this.shouldAutoSizeColumnsWhenModelUpdated = true;
            this.dataLoadedSinceColAutosize = false;  // reset this so we know to autosize again when data loads
            this.handleAnyColumnStateChange();
        },
        resetColumns() {
            console.log('resetColumns');  // does not reset column widths if those were changed, since you didn't set it the original coldefs
            // reset original colDefs -- ORIGINAL
            this.gridColumnApi?.resetColumnState();
            // may also have to do resetColumnGroupState()
            this.handleAnyColumnStateChange();  // save that new state!
        },
        reloadRows(purge = true) {
            // WE MAY BE CALLING THIS FROM THE PARENT, so DO NOT CHANGE THE NAME
            // purge = https://www.ag-grid.com/archive/25.3.0/vue-grid/server-side-model-refresh/
            // tell grid provider to reload current rows -- also reset filters/groupings???
            // you may not need to always purge the data... but we had purge as the default originally, so keeping that as the default here
            this.gridApi?.refreshServerSideStore({ purge });  // purge will remove all rows and reload from scratch
        },
        showNoRowsOverlay() {
            this.gridApi?.showNoRowsOverlay();
        },
        showLoadingOverlay() {
            this.gridApi?.showLoadingOverlay();
        },
        hideOverlay() {
            this.gridApi?.hideOverlay();
        },
        onCellDoubleClicked(params) {  // for our debug, leave on 
            console.log('onCellDoubleClicked', params);
        },
        exportExcelClientSideData() {  // WITH SSRM, THIS ONLY LOADS THE THINGS ALREADY LOADED IN THE GRID, SO MUST CHECK IF ALL IS LOADED FIRST!
            // see docs: https://www.ag-grid.com/archive/25.3.0/vue-grid/excel-export-api/#interfaces
            const excelExportParams = {
                processCellCallback: (params) => {
                    // this should return a string to use in the export 
                    //console.log('exportExcelParams', params);
                    if (params.column.colDef.refData && params.value) {
                        // do we have refData?  grid does not use it when exporting to Excel by default... so we do it manually here
                        return params.column.colDef.refData[params.value];
                    } else {
                        return params.value;
                    }
                },
            };
            this.gridApi.exportDataAsExcel(excelExportParams);
        },
        async startServerSideExcelDownload() {
            // trigger whatever process you're using to do SSR Excel downloads (processed by Celery, etc)
            // check the number of total items first, use that to know if we should do it sync or async?
            // must also know if this class/viewset supports this -- have gridProvider tell us?
            if (!this.gridProvider.allowExcelDownload) {
                console.log('Not allowed to download Excel file here');
                return;
            }
            const maxRowsForSyncDownload = 1000;  // if we have more than this, we will block for now -- later do async download
            if (this.filteredItemCount > maxRowsForSyncDownload) {
                console.log('TOO MANY ROWS FOR SYNC DOWNLOAD');
                // show modal message
                await this.$bvModal.msgBoxOk(`You can only download ${maxRowsForSyncDownload} rows of data at once, at this time.  Please change your filters to limit the number of rows, then try again.`, {
                    title: 'Too many rows',
                    size: 'sm',
                    buttonSize: 'md',
                    okVariant: 'primary',
                    headerClass: 'p-2 border-bottom-0',
                    footerClass: 'p-2 border-top-0',
                    centered: true
                });
                //await this.gridProvider.startExcelDownload();
                return;
            }
            // TODO - pass this to backend to only get these cols!!!
            const displayedCols = this.gridColumnApi.getAllDisplayedColumns();
            const excludeColIds = [ACTIONS_COLUMN_KEY];  // exclude action column from download (backend will do that anyways, but no need to pass it)
            const displayedColIds = displayedCols.map(c => c.colId).filter(colId => !excludeColIds.includes(colId));  // should be the same col names (path) as in backend
            console.log('displayedColIds', displayedColIds);
            await this.gridProvider.getExcelDownload({visibleFields: displayedColIds});  // we have computeds that pull flags from the gridProvider to watch loading state, etc.
        },


        // PRESETS

        handlePresetSelection(newPresetObj) {  // pass in null or {} for clearing presets
            //console.log('handlePresetSelection', newPresetObj);
            this.gridPresetSelected = cloneDeep(newPresetObj);
            // the v-model goes through here - must update this.gridPresetSelected
            // the ACTUAL preset values are inside the object.values
            const preset = cloneDeep(newPresetObj?.value ?? {});  // clone it right away to prevent problems
            //console.log('new preset object', preset);

            // get the values for new filters/grouping -- and stringify it before setting anything (for change detection)
            // DEFAULTS - if no filters or grouping
            const noFiltersValue = null;  // default to null - that's what docs say for reset (not {})
            const noGroupingValue = [];
            // FILTERS -- did they change?
            const newFilters = preset?.filters ?? noFiltersValue;  // default to oldFilters if not specified????
            const oldFilters = this.gridApi?.getFilterModel() ?? {};  // returns {} if no filters
            //console.log('newFilters', JSON.stringify(newFilters), 'oldFilters', JSON.stringify(oldFilters));
            const filterDidChange = !isEqual(newFilters, oldFilters);
            //console.log(`${filterDidChange ? 'FILTERS CHANGED IN PRESET' : 'NO FILTER CHANGE IN PRESET'}`, 'newFilters:', JSON.stringify(newFilters));
            // GROUPING -- did they change?
            const newGroupedColIds = preset?.grouping ?? noGroupingValue;  // default to oldGrouping if not specified????
            const oldGroupedColIds = this.gridColumnApi?.getRowGroupColumns()?.map(c => c.colId) ?? [];
            //console.log('newGroupedColIds', JSON.stringify(newGroupedColIds), 'oldGroupedColIds', JSON.stringify(oldGroupedColIds));
            const groupingDidChange = !isEqual(newGroupedColIds, oldGroupedColIds);  // this might not check ordering...
            //console.log(`${groupingDidChange ? 'GROUPING CHANGED IN PRESET' : 'NO GROUP CHANGE IN PRESET'}`, 'newGroupedColIds:', JSON.stringify(newGroupedColIds));
            // NOTE - if you set a preset that includes a 'set' filter, and it has values that there is NO DATA FOR, THE GRID REMOVES THE VALUE
            // SO IF YOU COMPARE THE PRESET TO THE ACTUAL FILTER, IT MIGHT BE DIFFERENT, JUST BECAUSE THE GRID REMOVED THAT VALUE!
            // SO.. we will capture the new filters in here JUST AFTER SETTING THEM - NOT BEFORE 
            if (this.gridApi && filterDidChange) {
                this.gridApi.setFilterModel(newFilters);
            }
            if (this.gridColumnApi && groupingDidChange) {
                this.gridColumnApi.setRowGroupColumns(newGroupedColIds);
            }
        },
        updateLastSetFilters() {
            // call this anytime you update the grid filters to stash a local copy 
            // call this only from handleFilterChanged???
            console.log("UPDATING LAST SET FILTERS");
            const currentGroupedColIds = this.gridColumnApi?.getRowGroupColumns()?.map(c => c.colId) ?? [];
            const currentFiltersRaw = this.gridApi?.getFilterModel() ?? null;  // returns {} if no filters
            //console.log('currentGroupedColIds', currentGroupedColIds);
            //console.log('currentFiltersRaw', currentFiltersRaw);
            const currentFilters = currentFiltersRaw && isEmpty(currentFiltersRaw) ? null : currentFiltersRaw;  // set empty object to null to match our way of saying 'no filters'
            this.lastSetFiltersGroupingStringified = JSON.stringify({currentFilters, currentGroupedColIds});
            this.lastSetFiltersGrouping = cloneDeep({filters: currentFilters, grouping: currentGroupedColIds});  // default to {} for filters, so comparison works
        },
        resetPresetSelection() {
            // set this.gridPresetSelected to orignal value to clear the cascade select
            this.gridPresetSelected = null;  // does NOT trigger our handlePresetSelection... we do it here
            this.handlePresetSelection(this.gridPresetSelected);
        },
        resetFilters() {
            // might get called after grid is destroyed, or before its creatd - so might not have this.gridApi
            if (this.gridApi) {  
                // put stuff here that needs gridApi
                this.gridApi.setFilterModel(null);
            }
            // this.resetExternalFilters();
            this.resetPresetSelection();
            this.resetExtraFilters();
        },
        resetExtraFilters() {
            // reset all extra filters to their initial values
            Object.values(this.extraFilterRegistry).forEach(flt => {
                this.$set(this.extraFilterValueKeys, flt.field, flt.emptyValue);
            });
            // NOTE -- this WILL NOT UPDATE GRID -- you must call handleExtraFilterInput() for each filter to update grid
            // this should be used only when you update the grid filters directly / some other way 
        },
        handleExtraFilterInput(filterField, valueKey) {
            // handle input from any of the extra filters - valueKey will be the value from dropdown (only the key, not full object)
            // valueKey will be null if clearing the input 
            //console.log('handleExtraFilterInput', filterField, valueKey);
            this.$set(this.extraFilterValueKeys, filterField, valueKey);
            const filterInst = this.extraFilterRegistry[filterField];
            if (!filterInst) return;  // no filter registered for this field
            const existingFilters = this.gridApi?.getFilterModel() ?? {};
            let newFilters;
            if (filterInst.emptyValue === valueKey) {
                // clear the filter (if we have it set)
                if (existingFilters[filterField]) {
                    newFilters = cloneDeep(existingFilters);
                    delete newFilters[filterField];
                }
            } else {
                const builtFilter = {[filterField]: filterInst.buildFilter(valueKey)};
                newFilters = Object.assign({}, existingFilters, builtFilter);
                //console.log('builtFilter', builtFilter);  // might be null, then don't set filter, or unset filter?
            }
            this.gridApi?.setFilterModel(newFilters);
            //this.updateLastSetFilters();  // let handleFilterChanged() handle it?
        },
        isExtraFilterSet(filterField) {
            // pass in the field name of an 'extra' filter, return boolean if it is set to something other than its emptyValue or not
            const filterInst = this.extraFilterRegistry[filterField];
            if (!filterInst) return false;  // no filter registered for this field
            return this.extraFilterValueKeys[filterField] !== filterInst.emptyValue;
        },
        
        // LOCAL STORAGE PREFERENCES
        // NOTE - column GROUP state may be different?  use gridColumnApi.getColumnGroupState() / setColumnGroupState() to manage that
        // also might want to store filter model, row grouping, etc....
        handleAnyColumnStateChange() {
            // call this from any other event handler that changes the column state - so we can save it to local storage
            // first, check if we're even supposed to save it based on prop
            if (!this.finalColStateLocalStorageKey) {
                return;
            }
            // inside here, we call things via a debouned function so we don't save it too often
            this.debouncedSaveCurrentColumnStateToLocalStorage();  // create this in created() hook to add the debounce!
        },
        saveCurrentColumnStateToLocalStorage() {
            console.log('###### STARTING SAVE CURRENT COLUMN STATE TO LOCAL STORAGE');
            // don't call this directly.... call via debounced version created in created() hook
            // get the current column state, then save it to local storage
            const columnStateParams = this.getCurrentColumnStateParamsFromGrid();
            this.setColumnStateParamsInLocalStorage(columnStateParams);
        },
        cleanColumnStateArray(colStateArray) {
            // helper to remove certain properties from colStates before saving to local storage, or before applying to grid
            // we want to remove things like pivot, rowGroup, sort, etc. - we only want to save the properties we care about
            // doing it in both directions to be safe
            const propertiesToExclude = ['pivot', 'pivotIndex', 'rowGroup', 'rowGroupIndex', 'sort', 'sortIndex'];  // we don't want to save these
            const allColStates = colStateArray && Array.isArray(colStateArray) ? colStateArray : [];
            const cleanedColStates = allColStates.map(colState => {
                return Object.keys(colState).reduce((acc, key) => {
                    if (!propertiesToExclude.includes(key)) {
                        acc[key] = colState[key];
                    }
                    return acc;
                }, {});
            });
            return cleanedColStates;
        },
        getCurrentColumnStateParamsFromGrid() {
            // get the current column state, but only for the properties we want to save - ag-grid handles smart merging of these when we re-load it
            // returns agGrid's ApplyColumnStateParams interface
            const allColStates = this.gridColumnApi?.getColumnState() || [];
            const colStateToSave = this.cleanColumnStateArray(allColStates);
            // TODO - combine with other things we want to save - like filter model, row grouping, etc.
            // just for testing now.... maybe separate these out, call them and pass to the setLocalStorage function....
            // const currentFilters = cloneDeep(this.currentFilters);
            // const currentGrouping = cloneDeep(this.currentGroupingCols);
            return {
                state: colStateToSave,
                applyOrder: true,  // apply in the order they are in the array -- hard-coded for now
            };
        },
        setColumnStateParamsInLocalStorage(columnStateParams) {
            // call getCurrentColumnStateParamsFromGrid() first, then pass it in here to save it to local storage
            const key = this.finalColStateLocalStorageKey;
            if (!key) {
                console.error('No localStorageKey set for ObjectGrid, cannot save columnStateParams');
                return;
            }
            const paramsString = JSON.stringify(columnStateParams);
            console.log('>>> Setting local storage columnStateParams from ObjectGrid for key:', key);
            localStorage.setItem(key, paramsString);
        },
        applySavedColumnStateParamsToGrid(columnStateParams) {
            console.log('###### APPLYING SAVED CURRENT COLUMN STATE FROM LOCAL STORAGE', columnStateParams);
            // deconstruct, make sure we got what we expected before doing it
            const { state, applyOrder } = columnStateParams;
            if (!state || !Array.isArray(state)) {
                console.error('applySavedColumnStateParamsToGrid - invalid colum state, so bailing out', state);
                return;
            }
            const cleanedColStates = this.cleanColumnStateArray(state);
            const paramsToApply = {
                state: cleanedColStates,
                applyOrder: applyOrder ?? true,  // default to true if not set
            }
            // apply a saved column state to the grid (Array of col states)
            //console.log('applySavedColumnState', columnState);
            this.gridColumnApi?.applyColumnState(paramsToApply);
        },
        getColumnStateParamsFromLocalStorage() {
            const key = this.finalColStateLocalStorageKey;
            const defaultParams = null;
            // extra verbose so we can see what's happening....
            if (!key) {
                console.log('No localStorageKey set for ObjectGrid, cannot load columnStateParams');
                return defaultParams;
            }
            const paramsRaw = localStorage.getItem(key);
            if (paramsRaw) {
                const parsedParams = JSON.parse(paramsRaw);
                console.log('Found columnStateParams in local storage:', parsedParams);
                return parsedParams;
            } else {
                console.log('No columnStateParams found in local storage, but we had the key');
                return defaultParams;
            }
        },
        clearLocalStorageColumnStateParams() {
            const key = this.finalColStateLocalStorageKey;
            return key ? localStorage.removeItem(key) : null;
        },
    },
}
</script>

<style lang="scss">

.object-grid-wrapper {
    .quick-filter {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        .filter-label {
            display: block;
            color: $gray-500;
            margin-bottom: 0.2rem;
            text-transform: uppercase;
            font-size: 0.7rem;
            font-weight: 500;
        }
        // PrimeVue components
        .p-cascadeselect-item-text,
        .p-cascadeselect-label,
        .p-dropdown-item,
        .p-dropdown-label {
            font-size: 0.8rem;
        }
    }

    .ag-header-row {
        .ag-header-icon.ag-header-label-icon.ag-filter-icon > .ag-icon.ag-icon-filter {
            color: blue;
        }
    }

    .number-cell {
        text-align: right;
    }
    .centered-cell {
        text-align: center;
    }

    .object-grid-action-header-cell,
    .object-grid-action-cell {
        text-align: center;
        padding-left: 8px;
        padding-right: 8px;
    }

    .ag-pinned-right-cols-container {
        box-shadow: -0.5rem 0rem 0.5rem -0.25rem rgba(0, 0, 0, 0.15) !important;  /* add shadow to make it clear there are other cols? or only if there are more?*/
        .ag-row {
            background-color: var(--gray-100) !important;
            &.ag-row-hover {
                background-color: var(--gray-200) !important;
            }
        }
    }
}

</style>